import vehiclesData from "../data/vehicles.json";
import { ColorMap } from "../utils/colors";
import type {
	VehiclesData,
	VehicleData,
	VehicleUIProps,
	ModelUIProps,
	VersionUIProps,
	ColorUIProps,
	VersionData,
} from "../types/vehicle";

const data = vehiclesData as unknown as VehiclesData;

export const getVehicleData = (vehicleId: string): VehicleData | null => {
	return data[vehicleId] || null;
};

export const getModelTypes = (vehicle: VehicleData): string[] => {
	return Object.keys(vehicle.models);
};

export const getVersions = (
	vehicle: VehicleData,
	modelType: string
): string[] => {
	return Object.keys(vehicle.models[modelType] || {});
};

export const getVersionData = (
	vehicle: VehicleData,
	modelType: string,
	versionName: string
): VersionData | null => {
	return vehicle.models[modelType]?.[versionName] || null;
};

export const getColorsList = (versionData: VersionData): string[] => {
	return Object.keys(versionData.gallery.colors);
};

export const getColorData = (versionData: VersionData, colorName: string) => {
	return versionData.gallery.colors[colorName];
};

export const getAllVehicles = (): string[] => {
	return Object.keys(data);
};

export const getVehicleDisplayName = (vehicleId: string): string => {
	return getVehicleData(vehicleId)?.exibition || vehicleId;
};

export const getVehicleOptions = (): { id: string; label: string }[] => {
	return getAllVehicles()
		.map((id) => ({ id, label: getVehicleDisplayName(id) }))
		.sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
};

export const getAllVersionsForVehicle = (vehicleId: string): string[] => {
	const vehicle = getVehicleData(vehicleId);
	if (!vehicle) return [];

	const versions = new Set<string>();
	Object.values(vehicle.models).forEach((modelVersions) => {
		Object.keys(modelVersions).forEach((version) => versions.add(version));
	});

	return Array.from(versions).sort((a, b) => a.localeCompare(b, "pt-BR"));
};

export const parseOfferVehicleName = (
	vehicleName: string
): { vehicleId: string; version: string } => {
	if (!vehicleName) {
		return { vehicleId: "", version: "" };
	}

	if (vehicleName.includes("::")) {
		const [vehicleId, version] = vehicleName.split("::");
		return { vehicleId, version: version || "" };
	}

	if (isValidVehicle(vehicleName)) {
		return { vehicleId: vehicleName, version: "" };
	}

	const matchedVehicle = getAllVehicles().find(
		(id) =>
			getVehicleDisplayName(id).toLowerCase() === vehicleName.toLowerCase()
	);

	return { vehicleId: matchedVehicle || vehicleName, version: "" };
};

export const formatOfferVehicleName = (
	vehicleId: string,
	version: string
): string => {
	if (!vehicleId) return "";
	if (!version) return vehicleId;
	return `${vehicleId}::${version}`;
};

export const isValidVehicle = (vehicleId: string): boolean => {
	return vehicleId in data;
};

export const isValidOfferVehicleVersion = (
	vehicleId: string,
	version: string
): boolean => {
	if (!isValidVehicle(vehicleId)) return false;
	if (!version) return true;

	const vehicle = getVehicleData(vehicleId);
	if (!vehicle) return false;

	return Object.values(vehicle.models).some(
		(modelVersions) => Boolean(modelVersions[version])
	);
};

const OFFER_CARD_IMAGE_PATTERN = /\.(?:webp|png|jpe?g)$/i;

export const getOfferCardImage = (
	vehicleId: string,
	version: string
): string => {
	const vehicle = getVehicleData(vehicleId);
	if (!vehicle || !version) return "";

	for (const modelVersions of Object.values(vehicle.models)) {
		const imageCardOffer = modelVersions[version]?.imageCardOffer?.trim();

		if (imageCardOffer && OFFER_CARD_IMAGE_PATTERN.test(imageCardOffer)) {
			return imageCardOffer;
		}
	}

	return "";
};

export const getOfferVehicleImage = (
	vehicleId: string,
	version: string
): string => {
	const vehicle = getVehicleData(vehicleId);
	if (!vehicle) return "";

	if (version) {
		const imageCardOffer = getOfferCardImage(vehicleId, version);
		if (imageCardOffer) return imageCardOffer;

		for (const modelVersions of Object.values(vehicle.models)) {
			const versionData = modelVersions[version];
			if (!versionData) continue;

			const versionOfferImage =
				versionData["img-oferta"] || versionData.gallery?.["img-oferta"];

			if (versionOfferImage) {
				return versionOfferImage;
			}
		}
	}

	if (vehicle["img-oferta"]) {
		return vehicle["img-oferta"];
	}

	if (version) {
		for (const modelVersions of Object.values(vehicle.models)) {
			const versionData = modelVersions[version];
			if (!versionData?.gallery?.colors) continue;

			const firstColor = Object.values(versionData.gallery.colors)[0];

			if (firstColor?.carrossel) {
				const carrossel = firstColor.carrossel;
				return Array.isArray(carrossel) ? carrossel[0] : carrossel;
			}
		}
	}

	return vehicle.external?.[0] || "";
};

export const getOfferVersionFeatures = (
	vehicleId: string,
	version: string
): string[] => {
	const vehicle = getVehicleData(vehicleId);
	if (!vehicle || !version) return [];

	let fallbackSpecs: string[] = [];

	for (const modelVersions of Object.values(vehicle.models)) {
		const versionData = modelVersions[version];
		if (!versionData?.features) continue;

		const specs = [
			versionData.features.performance,
			versionData.features.tecnologia,
			versionData.features.design,
		].filter(Boolean);

		if (!specs.length) continue;

		const hasOfferImage =
			Boolean(versionData["img-oferta"]) ||
			Boolean(versionData.gallery?.["img-oferta"]);

		if (hasOfferImage) {
			return specs;
		}

		if (!fallbackSpecs.length) {
			fallbackSpecs = specs;
		}
	}

	return fallbackSpecs;
};

export const getVehicleImages = (vehicleId: string) => {
	const vehicle = getVehicleData(vehicleId);
	if (!vehicle) return { internal: [], external: [] };

	return {
		internal: vehicle.internal || [],
		external: vehicle.external || [],
	};
};

export const formatColorName = (colorName: string): string => {
	return colorName.toLowerCase().replace(/\s+/g, "-");
};

export const getColorHex = (colorName: string): string => {
	const formattedName = formatColorName(colorName);
	return ColorMap[formattedName as keyof typeof ColorMap]?.hex || "#000000";
};

export const formatVehicleDataForUI = (
	vehicleData: VehicleData
): VehicleUIProps => {
	const formattedModels: ModelUIProps[] = Object.entries(vehicleData.models)
		.map(([modelName, modelVersions]) => {
			const versions: VersionUIProps[] = Object.entries(modelVersions)
				.map(([versionName, versionData]) => {
					const colors: ColorUIProps[] = Object.entries(
						versionData?.gallery?.colors || {}
					)
						.map(([colorName, colorData]) => ({
							name: colorName,
							hex: getColorHex(colorName),
							carrossel: colorData.carrossel,
							images: [],
						}))
						.sort((a, b) => a.name.localeCompare(b.name));

					return {
						name: versionName,
						tech: versionData?.tech ?? null,
						features: versionData?.features ?? null,
						colors,
					};
				})
				.sort((a, b) => a.name.localeCompare(b.name));

			return {
				name: modelName,
				versions,
			};
		})
		.sort((a, b) => a.name.localeCompare(b.name));

	function sanitizeData<T>(obj: T): T {
		if (obj === null || obj === undefined) return null as T;
		if (Array.isArray(obj)) return obj.map(sanitizeData) as T;
		if (typeof obj === "object") {
			const sanitized = {} as T;
			for (const [key, value] of Object.entries(obj)) {
				sanitized[key as keyof T] = sanitizeData(value);
			}
			return sanitized;
		}
		return obj;
	}

	return sanitizeData({
		exibition: vehicleData.exibition ?? null,
		name: vehicleData.name,
		models: formattedModels.map((model) => ({
			...model,
			versions:
				model.versions?.map((version) => ({
					...version,
					tech: version.tech ?? null,
				})) ?? [],
		})),
		external: vehicleData.external,
		internal: vehicleData.internal,
		// Campos específicos para Hiace (galerias por versão)
		externalMin: vehicleData.externalMin,
		internalMin: vehicleData.internalMin,
		externalFur: vehicleData.externalFur,
		internalFur: vehicleData.internalFur,
	});
};
